<script setup>
import { ref, onMounted } from 'vue';
import { Pagination, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

const showVideo = ref(false);
const linkUrl = ref('');

let props = defineProps({
    data: Object,
});

const detectDevice = () => {
    const userAgent = navigator.userAgent;

    if (/(iPhone|iPad|Macintosh)/i.test(userAgent)) {
        linkUrl.value =
            'https://apps.apple.com/tr/app/wodo-wallet/id6475333989';
    } else {
        linkUrl.value =
            'https://play.google.com/store/apps/details?id=com.wodowallet';
    }
};

onMounted(() => {
    detectDevice();
});
</script>

<template>
    <div class="bg-wgn-purple-6">
        <div class="flex w-full flex-col justify-between bg-wgn-purple-6">
            <div class="w-full">
                <section
                    class="overflow-hidden pt-[45px] pb-[100px] md:pt-[90px] md:pb-[200px]"
                >
                    <div
                        class="container relative mx-auto flex max-w-screen-xl flex-col justify-between px-20 md:flex-row 2xl:pl-0"
                    >
                        <img
                            class="absolute bottom-0 right-0 md:-top-28 md:block md:max-w-[1035px] md:scale-100"
                            src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/new-section1-background.png"
                            alt="Wodo Finance Shadow"
                        />

                        <div
                            class="z-10 flex max-w-[635px] flex-col justify-center"
                        >
                            <div class="mb-8 md:mb-[72px]">
                                <div
                                    class="mb-4 text-xl font-semibold leading-6 tracking-[1%] text-wgn-red-2"
                                >
                                    {{ data.new_section_1.slogan }}
                                </div>
                                <div
                                    class="mb-2 text-4xl font-extrabold tracking-[1%] text-wgn-white-1 md:text-[40px] md:leading-[52px]"
                                >
                                    {{ data.new_section_1.title }}
                                </div>
                                <div
                                    class="mb-10 text-lg tracking-[1%] text-wgn-white-2"
                                >
                                    {{ data.new_section_1.description }}

                                    <div
                                        class="mt-1 text-sm italic text-wgn-white-2"
                                    >
                                        {{ data.section_0.coming_soon }}
                                    </div>
                                </div>

                                <a
                                    :href="data.new_section_1.btn_url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="gradient-button inline-block rounded-xl border border-wgn-purple-7 p-5 text-center text-lg font-medium text-white"
                                    >{{ data.new_section_1.btn_txt }}
                                </a>
                            </div>

                            <div class="flex">
                                <a
                                    :href="data.new_section_1.app_store_url"
                                    class="mr-8"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    <img
                                        :src="data.new_section_1.app_store_img"
                                        alt="App Store"
                                    />
                                </a>

                                <a
                                    :href="data.new_section_1.google_play_url"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    <img
                                        class=""
                                        :src="
                                            data.new_section_1.google_play_img
                                        "
                                        alt="Google Store"
                                    />
                                </a>
                            </div>
                        </div>

                        <div class="relative flex items-center justify-center">
                            <img
                                :src="data.new_section_1.mobile_image"
                                alt="Wodo Mobile App"
                                class="relative scale-50 opacity-0 sm:opacity-100 md:scale-100"
                            />
                            <img
                                :src="data.new_section_1.card_image"
                                alt="Wodo Card"
                                class="absolute bottom-9 md:-left-[52%]"
                            />
                            <div
                                class="absolute -bottom-4 text-3xl font-bold text-wgn-purple-10 md:-left-1/3"
                            >
                                {{ data.new_section_1.card_text }}
                            </div>
                        </div>
                    </div>
                </section>

                <section class="mb-[140px] md:mb-[280px]">
                    <hr
                        class="mx-auto mb-[100px] block h-[1px] w-1/2 border-t border-wgn-purple-4"
                    />

                    <div
                        class="relative mb-24 flex flex-col items-center justify-center"
                    >
                        <div
                            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                        >
                            <img
                                class=""
                                src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/new-section2-light3.png"
                                alt="Wodo Finance Shadow"
                            />
                        </div>
                        <div
                            class="text-white-1 z-10 mb-2 text-xl font-semibold text-wgn-white-1"
                        >
                            {{ data.new_section_2.intro }}
                        </div>
                        <div
                            class="z-10 text-4xl tracking-[2%] text-wgn-orange-2 md:text-[52px] md:leading-[62px]"
                        >
                            {{ data.new_section_2.number }}
                        </div>
                        <div
                            class="z-10 text-xl tracking-[2%] text-wgn-orange-3 md:-mt-1"
                        >
                            {{ data.new_section_2.outro }}
                        </div>

                        <div
                            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                        >
                            <div
                                class="text-[70px] font-extrabold tracking-[5%] text-wgn-orange-2 opacity-[0.08] md:text-[140px] md:leading-[160px]"
                            >
                                {{ data.new_section_2.number }}
                            </div>
                        </div>
                    </div>

                    <div class="container mx-auto max-w-screen-2xl 2xl:pl-0">
                        <div class="flex gap-12">
                            <swiper
                                class="grid w-full grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
                                :breakpoints="{
                                    1: { slidesPerView: 3 },
                                    640: { slidesPerView: 6 },
                                    1024: { slidesPerView: 8 },
                                    1280: { slidesPerView: 10 },
                                }"
                                :autoplay="true"
                                :space-between="16"
                                :loop="true"
                                :modules="[Pagination, Autoplay]"
                            >
                                <swiper-slide
                                    v-for="outlet in data.new_section_2.outlets"
                                    :key="outlet.number"
                                >
                                    <div
                                        class="mx-auto flex h-[108px] w-[108px] items-center justify-center rounded-full"
                                        style="
                                            background-image: url('https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/outlets/outlet-background.png');
                                        "
                                    >
                                        <img
                                            :src="outlet.img"
                                            alt="Outlet logo"
                                        />
                                    </div>
                                </swiper-slide>
                            </swiper>
                        </div>

                        <!-- <div
                            class="flex h-[108px] w-[108px] items-center justify-center rounded-full"
                            style="
                                background-image: url('https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/outlets/outlet-background.png');
                            "
                        >
                            <img
                                :src="data.new_section_2.outlet_2"
                                alt="Outlet logo"
                            />
                        </div> -->
                    </div>
                </section>

                <section
                    class="mx-auto mb-[130px] md:mb-[260px]"
                    style="
                        background-repeat: repeat;
                        background-position: center;
                        background-size: cover;
                        background-image: url('https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/new-section3-background.png');
                    "
                >
                    <div
                        class="container mx-auto flex flex-col-reverse justify-between px-10 py-10 md:flex-row md:py-14"
                    >
                        <div class="max-w-[475px]">
                            <div
                                class="gradient-slogan mb-2 text-xl font-semibold tracking-[1%]"
                            >
                                {{ data.new_section_3.slogan }}
                            </div>
                            <div
                                class="mb-4 text-3xl font-extrabold tracking-[1%] text-wgn-white-1"
                            >
                                {{ data.new_section_3.title }}
                            </div>
                            <p class="mb-2 text-xl text-wgn-white-1">
                                {{ data.new_section_3.description_1 }}
                            </p>
                            <p class="mb-10 text-xl text-wgn-white-1">
                                {{ data.new_section_3.description_2 }}

                                <br />
                                <span
                                    class="mt-1 text-sm italic text-wgn-white-1"
                                >
                                    ({{ data.section_0.coming_soon }})
                                </span>
                            </p>

                            <a
                                :href="data.new_section_3.btn_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="gradient-button inline-block rounded-xl border border-wgn-purple-7 p-5 text-center text-lg font-medium text-white"
                                >{{ data.new_section_3.btn_txt }}
                            </a>
                        </div>

                        <div
                            class="mb-10 flex max-w-[535px] items-center md:mb-0"
                        >
                            <img
                                :src="data.new_section_3.cards"
                                alt="Wodo Cards"
                            />
                        </div>
                    </div>
                </section>

                <section class="mx-auto mt-[45px] md:mt-[90px]">
                    <div
                        class="container mx-auto bg-wgn-purple-12 px-6 py-10 md:py-14 md:px-12"
                    >
                        <div class="mb-12">
                            <div
                                class="mb-2 text-center text-xl font-semibold text-wgn-red-1"
                            >
                                {{ data.new_section_4.intro }}
                            </div>
                            <div
                                class="mb-5 text-center text-3xl font-extrabold tracking-[1%] text-wgn-white-1"
                            >
                                {{ data.new_section_4.title }}
                            </div>
                            <div class="text-center text-xl text-wgn-white-1">
                                {{ data.new_section_4.description }}
                            </div>
                        </div>

                        <!-- style="
                                    background-repeat: repeat;
                                    background-image: url('https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/new-section3-card-background.png');
                                " -->

                        <div
                            class="grid grid-cols-1 gap-11 sm:grid-cols-2 lg:grid-cols-4"
                        >
                            <div
                                :key="step.number"
                                v-for="step in data.new_section_4.steps"
                                class="gradient-card flex min-h-[375px] flex-col justify-between rounded-lg border-2 border-wgn-purple-7"
                            >
                                <div
                                    class="flex flex-col items-center px-4 pt-8"
                                >
                                    <div class="mb-4 text-wgn-white-1">
                                        <span
                                            class="h-6 w-6"
                                            v-html="step.icon"
                                        ></span>
                                    </div>

                                    <div
                                        class="mb-8 text-center text-2xl font-medium text-wgn-white-1"
                                    >
                                        {{ step.title }}
                                    </div>

                                    <div
                                        class="mb-4 text-center text-xl text-wgn-gray-6"
                                    >
                                        {{ step.description }}
                                    </div>
                                </div>

                                <div
                                    class="mr-4 mb-5 px-5 text-center text-2xl font-extrabold text-wgn-purple-11"
                                >
                                    STEP {{ step.number }}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="relative mx-auto mt-[45px] md:mt-[90px]">
                    <!-- <img
                        class="absolute top-0 left-0 hidden md:block"
                        src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section1-shadow1.png"
                        alt="Wodo Finance Shadow"
                    />
                    <img
                        class="absolute top-0 right-0 hidden md:block"
                        src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section1-shadow2.png"
                        alt="Wodo Finance Shadow"
                    /> -->
                    <div
                        class="container mx-auto mt-4 flex max-w-screen-2xl flex-1 flex-grow flex-col overflow-hidden px-4 pt-[45px] md:mt-8 md:pt-[90px]"
                    >
                        <div class="">
                            <h1
                                class="gradient-slogan mb-4 text-center text-xl font-semibold uppercase"
                            >
                                {{ data.section_1.title }}
                            </h1>
                            <!-- <h1 class="gradient-slogan gradient-slogan-big mb-8">
                                {{ data.section_1.title_2 }}
                            </h1> -->
                            <h1
                                class="mx-auto mb-10 max-w-[700px] text-center text-xl text-wgn-white-1"
                            >
                                {{ data.section_1.content }}
                            </h1>
                        </div>

                        <div class="w-full">
                            <div class="relative">
                                <!-- <div
                                    class="absolute h-full w-full"
                                    style="
                                        background-size: contain;
                                        background-position: center;
                                        transform: scale(1.5);
                                        background-repeat: no-repeat;
                                        background-image: url('https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section1-shadow0.png');
                                    "
                                ></div> -->
                                <img
                                    class="relative z-10 m-auto w-3/4 pt-[70px] pb-[60px] md:w-1/2"
                                    :src="data.section_1.image"
                                    :alt="data.section_1.title"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <section class="relative mx-auto mb-[50px] mt-10 md:mb-[115px]">
                    <img
                        class="absolute bottom-0 left-0 hidden md:block"
                        src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section1-shadow3.png"
                        alt="Wodo Finance Shadow"
                    />
                    <img
                        class="absolute bottom-0 right-0 hidden md:block"
                        src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section1-shadow4.png"
                        alt="Wodo Finance Shadow"
                    />
                    <!-- <img
                        class="m-auto mb-5 h-[30px] max-w-[1024px]"
                        src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section1-stars.png"
                        alt="Wodo Finance Stars"
                    />
                    <p class="text-center">
                        <a
                            :href="data.section_1.direction_link"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="gradient-button mb-6 inline-block min-w-[210px] rounded-xl border border-wgn-purple-7 py-5 px-10 text-lg font-medium text-white"
                            >{{ data.section_1.btn }}
                        </a>
                    </p>
                    <p class="text-center">
                        <a
                            :href="linkUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="gradient-button mt-5 inline-block min-w-[210px] rounded-xl border border-wgn-purple-7 py-5 px-10 text-lg font-medium text-white md:mt-0"
                            >{{ data.section_1.btn_2 }}
                        </a>
                    </p> -->
                </section>

                <!-- <section
                    class="container relative mx-auto max-w-screen-2xl py-[60px] md:py-[120px]"
                >
                    <div
                        style="
                            opacity: 0.32;
                            background: #ffffff;
                            mix-blend-mode: overlay;
                            filter: blur(80px);
                            left: 0;
                            top: 0;
                            position: absolute;
                            width: 100%;
                            height: 100%;
                        "
                    ></div>
                    <div class="relative mx-5 md:mx-12">
                        <img
                            class="absolute top-0 left-0 h-full w-full"
                            src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section2-bg.png"
                            alt="Wodo Finance Shadow"
                        />

                        <div
                            class="relative flex flex-col items-center justify-between pt-[75px] pl-[65px] pr-[60px] md:flex-row"
                        >
                            <div class="">
                                <h2
                                    class="mb-4 text-3xl font-medium tracking-[0.25px] text-white"
                                >
                                    {{ data.section_2.name }}
                                </h2>
                                <p
                                    class="mb-4 text-base font-light text-white lg:mb-0"
                                >
                                    {{ data.section_2.title }}
                                </p>
                            </div>
                            <div class="">
                                <img
                                    class="m-auto max-w-[100%] pb-2"
                                    :src="data.section_2.image"
                                    :alt="data.section_2.name"
                                />
                            </div>
                        </div>
                    </div>
                </section> -->

                <section
                    class="mx-5 max-w-screen-2xl pt-[50px] pb-[80px] md:mx-20 md:pb-[165px] md:pt-[115px] 2xl:mx-auto"
                >
                    <!-- <div class="mb-[50px] md:mb-[170px]">
                        <h2
                            class="mb-6 text-center text-3xl font-semibold leading-7 tracking-[2px] text-wgn-red-2"
                        >
                            {{ data.section_3.title }}
                        </h2>
                        <p
                            class="text-center text-xl font-medium leading-7 tracking-[0.2px] text-white"
                        >
                            {{ data.section_3.content }}
                        </p>
                    </div> -->

                    <div class="flex flex-col gap-7 md:gap-14 xl:flex-row">
                        <div
                            class="gradient-block relative w-full overflow-hidden rounded-xl border border-wgn-purple-8 px-[30px] py-[35px] md:mb-0 md:px-[90px] md:py-[100px] xl:w-3/5"
                        >
                            <img
                                class="absolute bottom-0 left-0 w-[180px] rotate-[16deg] brightness-200"
                                src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section3-f1-bg-item1.png"
                                alt="Wodo Finance Background Item 1"
                            />
                            <div
                                class="flex h-full flex-col items-center justify-between xl:flex-row"
                            >
                                <div class="mb-10 xl:mb-0">
                                    <div
                                        class="mb-3 text-center text-2xl font-medium text-white xl:w-4/5 xl:text-left"
                                    >
                                        {{ data.section_3.section_3_1.title }}
                                    </div>
                                    <div
                                        class="text-center text-xl leading-7 text-wgn-gray-4 text-opacity-50 xl:w-4/5 xl:text-left"
                                    >
                                        {{ data.section_3.section_3_1.content }}
                                    </div>
                                </div>

                                <div
                                    class="z-10 cursor-pointer"
                                    @click="showVideo = !showVideo"
                                >
                                    <img
                                        v-if="!showVideo"
                                        class="max-w-[1024px]"
                                        :src="data.section_3.section_3_1.image"
                                        :alt="data.section_3.section_3_1.title"
                                    />
                                    <img
                                        v-else
                                        class="m-auto max-w-[200px] rounded-xl"
                                        :src="data.section_3.section_3_1.gif"
                                        :alt="data.section_3.section_3_1.title"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            class="gradient-block relative w-full overflow-hidden rounded-xl border border-wgn-purple-8 px-[45px] py-[35px] md:px-[90px] md:py-[70px] xl:w-2/5"
                        >
                            <img
                                class="absolute right-[-50px] top-[-50px] w-[165px] rotate-[16deg] brightness-200"
                                src="https://s3.eu-central-1.amazonaws.com/static.wodo.io/network/finance/section3-f1-bg-item1.png"
                                alt="Wodo Finance Background Item 2"
                            />

                            <div class="flex flex-col items-center">
                                <div
                                    class="mb-3 text-center text-2xl font-medium text-white"
                                >
                                    {{ data.section_3.section_3_2.title }}
                                </div>
                                <div
                                    class="mb-10 text-center text-xl leading-7 text-wgn-gray-4 text-opacity-50"
                                >
                                    {{ data.section_3.section_3_2.content }}
                                </div>

                                <div
                                    class="center flex flex-col justify-center gap-6"
                                >
                                    <a
                                        :href="data.section_3.section_3_2.route"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="gradient-button inline-block rounded-xl border border-wgn-purple-7 p-5 text-center text-lg font-medium text-white"
                                        >{{ data.section_3.section_3_2.btn }}
                                    </a>

                                    <a
                                        :href="linkUrl"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="gradient-button inline-block rounded-xl border border-wgn-purple-7 p-5 text-center text-lg font-medium text-white"
                                        >{{
                                            data.section_3.section_3_2.btn_2
                                        }}</a
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="mx-auto max-w-screen-2xl">
                    <div
                        class="mb-16 mt-10 w-full border-b border-wgn-purple-4 md:mb-32 md:mt-0 xl:mb-10"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="mx-auto"
                            width="32"
                            height="16"
                            viewBox="0 0 32 16"
                            fill="none"
                        >
                            <g style="mix-blend-mode: luminosity">
                                <path
                                    d="M16 -1.69965e-08C7.16344 -7.60958e-09 7.60958e-09 7.16344 1.69965e-08 16L32 16C32 7.16344 24.8366 -2.63834e-08 16 -1.69965e-08Z"
                                    fill="#6100D4"
                                />
                            </g>
                            <g style="mix-blend-mode: luminosity">
                                <path
                                    d="M16 3C8.8203 3 3 8.8203 3 16L29 16C29 8.8203 23.1797 3 16 3Z"
                                    fill="#FF3815"
                                />
                            </g>
                            <g style="mix-blend-mode: luminosity">
                                <path
                                    d="M16 6C10.4772 6 6 10.4772 6 16L26 16C26 10.4772 21.5228 6 16 6Z"
                                    fill="#26E1ED"
                                />
                            </g>
                            <g style="mix-blend-mode: luminosity">
                                <path
                                    d="M16 9C12.134 9 9 12.134 9 16L23 16C23 12.134 19.866 9 16 9Z"
                                    fill="#FFA722"
                                />
                            </g>
                            <g style="mix-blend-mode: luminosity">
                                <path
                                    d="M16 11C13.7909 11 12 12.7909 12 15H20C20 12.7909 18.2091 11 16 11Z"
                                    fill="#18122A"
                                />
                            </g>
                        </svg>
                    </div>
                </section>

                <section
                    class="container mx-auto max-w-screen-2xl px-10 2xl:pl-0"
                >
                    <div class="mb-[115px]">
                        <div class="flex flex-col items-center lg:flex-row">
                            <div class="w-full flex-1">
                                <p
                                    class="mb-[4px] text-xl font-medium leading-7 tracking-[2px] text-wgn-red-2"
                                >
                                    {{ data.section_4.feature_1.key }}
                                </p>
                                <h2
                                    class="mb-7 text-[40px] font-bold capitalize leading-[49px] text-white"
                                >
                                    {{ data.section_4.feature_1.title }}
                                </h2>
                                <div class="flex">
                                    <div class="mr-2 mt-1.5">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M12 2L10.6985 7.20599C10.4445 8.22185 10.3176 8.72978 10.0531 9.14309C9.81915 9.50868 9.50868 9.81915 9.14309 10.0531C8.72978 10.3176 8.22185 10.4445 7.20599 10.6985L2 12L7.20599 13.3015C8.22185 13.5555 8.72978 13.6824 9.14309 13.9469C9.50868 14.1808 9.81915 14.4913 10.0531 14.8569C10.3176 15.2702 10.4445 15.7782 10.6985 16.794L12 22L13.3015 16.794C13.5555 15.7782 13.6824 15.2702 13.9469 14.8569C14.1808 14.4913 14.4913 14.1808 14.8569 13.9469C15.2702 13.6824 15.7782 13.5555 16.794 13.3015L22 12L16.794 10.6985C15.7782 10.4445 15.2702 10.3176 14.8569 10.0531C14.4913 9.81915 14.1808 9.50868 13.9469 9.14309C13.6824 8.72978 13.5555 8.22185 13.3015 7.20599L12 2Z"
                                                stroke="#FF5555"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <p class="text-2xl text-wgn-gray-5">
                                        {{ data.section_4.feature_1.content }}
                                    </p>
                                </div>
                            </div>
                            <div class="w-full flex-1">
                                <img
                                    class="m-auto w-3/4 md:w-full"
                                    :src="data.section_4.feature_1.image"
                                    :alt="data.section_4.feature_1.title"
                                />
                            </div>
                        </div>

                        <div
                            class="flex flex-col items-center lg:flex-row-reverse"
                        >
                            <div class="w-full flex-1">
                                <p
                                    class="mb-[4px] text-xl font-medium leading-7 tracking-[2px] text-wgn-orange-1"
                                >
                                    {{ data.section_4.feature_2.key }}
                                </p>
                                <h2
                                    class="mb-7 text-[40px] font-bold capitalize leading-[49px] text-white"
                                >
                                    {{ data.section_4.feature_2.title }}
                                </h2>
                                <div class="flex">
                                    <div class="mr-2 mt-1.5">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M12 2L10.6985 7.20599C10.4445 8.22185 10.3176 8.72978 10.0531 9.14309C9.81915 9.50868 9.50868 9.81915 9.14309 10.0531C8.72978 10.3176 8.22185 10.4445 7.20599 10.6985L2 12L7.20599 13.3015C8.22185 13.5555 8.72978 13.6824 9.14309 13.9469C9.50868 14.1808 9.81915 14.4913 10.0531 14.8569C10.3176 15.2702 10.4445 15.7782 10.6985 16.794L12 22L13.3015 16.794C13.5555 15.7782 13.6824 15.2702 13.9469 14.8569C14.1808 14.4913 14.4913 14.1808 14.8569 13.9469C15.2702 13.6824 15.7782 13.5555 16.794 13.3015L22 12L16.794 10.6985C15.7782 10.4445 15.2702 10.3176 14.8569 10.0531C14.4913 9.81915 14.1808 9.50868 13.9469 9.14309C13.6824 8.72978 13.5555 8.22185 13.3015 7.20599L12 2Z"
                                                stroke="#FFCA7B"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <p class="text-2xl text-wgn-gray-5">
                                        {{ data.section_4.feature_2.content }}
                                    </p>
                                </div>
                            </div>
                            <div class="w-full flex-1">
                                <img
                                    class="m-auto w-3/4 md:w-full"
                                    :src="data.section_4.feature_2.image"
                                    :alt="data.section_4.feature_2.title"
                                />
                            </div>
                        </div>

                        <div class="flex flex-col items-center lg:flex-row">
                            <div class="w-full">
                                <p
                                    class="mb-[4px] text-xl font-medium leading-7 tracking-[2px] text-wgn-purple-9"
                                >
                                    {{ data.section_4.feature_3.key }}
                                </p>
                                <h2
                                    class="mb-7 text-[40px] font-bold capitalize leading-[49px] text-white"
                                >
                                    {{ data.section_4.feature_3.title }}
                                </h2>
                                <div class="flex">
                                    <div class="mr-2 mt-1.5">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M12 2L10.6985 7.20599C10.4445 8.22185 10.3176 8.72978 10.0531 9.14309C9.81915 9.50868 9.50868 9.81915 9.14309 10.0531C8.72978 10.3176 8.22185 10.4445 7.20599 10.6985L2 12L7.20599 13.3015C8.22185 13.5555 8.72978 13.6824 9.14309 13.9469C9.50868 14.1808 9.81915 14.4913 10.0531 14.8569C10.3176 15.2702 10.4445 15.7782 10.6985 16.794L12 22L13.3015 16.794C13.5555 15.7782 13.6824 15.2702 13.9469 14.8569C14.1808 14.4913 14.4913 14.1808 14.8569 13.9469C15.2702 13.6824 15.7782 13.5555 16.794 13.3015L22 12L16.794 10.6985C15.7782 10.4445 15.2702 10.3176 14.8569 10.0531C14.4913 9.81915 14.1808 9.50868 13.9469 9.14309C13.6824 8.72978 13.5555 8.22185 13.3015 7.20599L12 2Z"
                                                stroke="#BBA4FF"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <p class="text-2xl text-wgn-gray-5">
                                        {{ data.section_4.feature_3.content }}
                                    </p>
                                </div>
                            </div>
                            <div class="w-full">
                                <img
                                    class="m-auto w-3/4 md:w-full"
                                    :src="data.section_4.feature_3.image"
                                    :alt="data.section_4.feature_3.title"
                                />
                            </div>
                        </div>

                        <div
                            class="flex flex-col items-center lg:flex-row-reverse"
                        >
                            <div class="w-full">
                                <p
                                    class="mb-[4px] text-xl font-medium leading-7 tracking-[2px] text-wgn-orange-2"
                                >
                                    {{ data.section_4.feature_4.key }}
                                </p>
                                <h2
                                    class="mb-7 text-[40px] font-bold capitalize leading-[49px] text-white"
                                >
                                    {{ data.section_4.feature_4.title }}
                                </h2>
                                <div class="flex">
                                    <div class="mr-2 mt-1.5">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M12 2L10.6985 7.20599C10.4445 8.22185 10.3176 8.72978 10.0531 9.14309C9.81915 9.50868 9.50868 9.81915 9.14309 10.0531C8.72978 10.3176 8.22185 10.4445 7.20599 10.6985L2 12L7.20599 13.3015C8.22185 13.5555 8.72978 13.6824 9.14309 13.9469C9.50868 14.1808 9.81915 14.4913 10.0531 14.8569C10.3176 15.2702 10.4445 15.7782 10.6985 16.794L12 22L13.3015 16.794C13.5555 15.7782 13.6824 15.2702 13.9469 14.8569C14.1808 14.4913 14.4913 14.1808 14.8569 13.9469C15.2702 13.6824 15.7782 13.5555 16.794 13.3015L22 12L16.794 10.6985C15.7782 10.4445 15.2702 10.3176 14.8569 10.0531C14.4913 9.81915 14.1808 9.50868 13.9469 9.14309C13.6824 8.72978 13.5555 8.22185 13.3015 7.20599L12 2Z"
                                                stroke="#FFA722"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <p class="text-2xl text-wgn-gray-5">
                                        {{ data.section_4.feature_4.content }}
                                    </p>
                                </div>
                            </div>
                            <div class="w-full">
                                <img
                                    class="m-auto w-3/4 md:w-full"
                                    :src="data.section_4.feature_4.image"
                                    :alt="data.section_4.feature_4.title"
                                />
                            </div>
                        </div>

                        <div class="flex flex-col items-center lg:flex-row">
                            <div class="w-full">
                                <p
                                    class="mb-[4px] text-xl font-medium leading-7 tracking-[2px] text-wgn-cyan-1"
                                >
                                    {{ data.section_4.feature_5.key }}
                                </p>

                                <h2
                                    class="mb-7 text-[40px] font-bold capitalize leading-[49px] text-white"
                                >
                                    {{ data.section_4.feature_5.title }}
                                </h2>

                                <div class="flex">
                                    <div class="mr-2 mt-1.5">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M12 2L10.6985 7.20599C10.4445 8.22185 10.3176 8.72978 10.0531 9.14309C9.81915 9.50868 9.50868 9.81915 9.14309 10.0531C8.72978 10.3176 8.22185 10.4445 7.20599 10.6985L2 12L7.20599 13.3015C8.22185 13.5555 8.72978 13.6824 9.14309 13.9469C9.50868 14.1808 9.81915 14.4913 10.0531 14.8569C10.3176 15.2702 10.4445 15.7782 10.6985 16.794L12 22L13.3015 16.794C13.5555 15.7782 13.6824 15.2702 13.9469 14.8569C14.1808 14.4913 14.4913 14.1808 14.8569 13.9469C15.2702 13.6824 15.7782 13.5555 16.794 13.3015L22 12L16.794 10.6985C15.7782 10.4445 15.2702 10.3176 14.8569 10.0531C14.4913 9.81915 14.1808 9.50868 13.9469 9.14309C13.6824 8.72978 13.5555 8.22185 13.3015 7.20599L12 2Z"
                                                stroke="#26E1ED"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </div>

                                    <p class="text-2xl text-wgn-gray-5">
                                        {{ data.section_4.feature_5.content }}
                                    </p>
                                </div>
                            </div>

                            <div class="w-full">
                                <img
                                    class="m-auto w-3/4 md:w-full"
                                    :src="data.section_4.feature_5.image"
                                    :alt="data.section_4.feature_5.title"
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<style>
.gradient-slogan {
    background: linear-gradient(
        180deg,
        #9770e5 0%,
        #cebfec 165.81%,
        rgba(238, 237, 240, 0.5) 261.11%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.gradient-slogan-big {
    font-size: 50px;
    font-style: normal;
    font-weight: 800;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: capitalize;
    text-align: center;
}

.gradient-button {
    background: linear-gradient(
        180deg,
        #ae8ae9 -69.35%,
        rgba(19, 3, 38, 0.25) 93.9%
    );
    box-shadow: 0px 8px 16px 0px rgba(151, 71, 255, 0.16);
}

.gradient-button:hover {
    background: linear-gradient(0deg, #230944 0%, #230944 100%),
        linear-gradient(180deg, #ae8ae9 -53.66%, rgba(19, 3, 38, 0.25) 93.9%);
}

.gradient-card {
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
        linear-gradient(
            180deg,
            rgba(255, 202, 123, 0.08) 0%,
            rgba(206, 191, 236, 0.08) 165.81%,
            rgba(238, 237, 240, 0.04) 261.11%
        );
}

.gradient-block {
    background: linear-gradient(
        104deg,
        #402c61 -51.61%,
        rgba(19, 3, 38, 0.72) 96.5%
    );
}
</style>
